import React, { createContext, useEffect, useMemo, useState } from 'react'

import { ContractNamesController } from '@ambire-common/controllers/contractNames/contractNames'
import { IContractNamesController } from '@ambire-common/interfaces/contractNames'

const ContractNamesContext = createContext<{
  state: IContractNamesController
  contractNamesCtrl: IContractNamesController
}>({
  state: {} as IContractNamesController,
  contractNamesCtrl: {} as IContractNamesController
})
/**
 * The in-process controller (mobile-web / benzin) has no StorageController, so
 * the DEX router registry is cached in localStorage instead — same 24h TTL,
 * just a different backing store. Everything is wrapped: a private window or
 * blocked site data throws on access and must not break name resolution.
 */
const localStorageAdapter = {
  get: async (key: string, defaultValue?: any) => {
    try {
      const raw = window.localStorage.getItem(key)
      return raw ? JSON.parse(raw) : defaultValue
    } catch {
      return defaultValue
    }
  },
  set: async (key: string, value: any) => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value))
    } catch {
      // Storage unavailable — the registry simply refetches next session.
    }
  }
}

const contractNamesCtrl = new ContractNamesController({
  fetch: fetch.bind(window) as any,
  storage: localStorageAdapter,
  debounceTime: 50
})

const ContractNamesContextProvider: React.FC<any> = ({ children }) => {
  const [state, setState] = useState<IContractNamesController>(contractNamesCtrl)

  useEffect(() => {
    if (!contractNamesCtrl) return

    contractNamesCtrl.onUpdate(() => {
      setState(contractNamesCtrl.toJSON())
    })
  }, [])

  const value = useMemo(
    () => ({
      state,
      contractNamesCtrl
    }),
    [state]
  )

  return <ContractNamesContext.Provider value={value}>{children}</ContractNamesContext.Provider>
}

export { ContractNamesContext, ContractNamesContextProvider }
